import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, RefreshCw, ImageOff, ChevronLeft, Save, ExternalLink, Loader2, Wrench, Upload, FileSearch } from "lucide-react";
import { toast } from "sonner";
import { efAdminListEntities, efAdminPatchEntity, efAdminRestoreBundledBatches, efAdminPublishRothschildLane, efAdminPublishFirstMixedLane, efAdminPublishFlightLogsStarter, efAdminUploadEntityImage, efAdminEnableZorroRanchStub } from "../../lib/api";
import { MediaAttachButton } from "../media/MediaAttachButton";
import { kindToMediaType } from "../media/mediaRoles";

/**
 * AdminEpsteinEntities — mobile-friendly entity browser + photo editor.
 *
 * UX:
 *   list view: full-width search + scrollable card list (works on phone)
 *   detail view: editable photo_url / thumbnail_url with live preview + Save
 *
 * Selecting a row swaps to the detail panel (no modal — full-screen on mobile,
 * so the keyboard doesn't fight a small dialog).
 */
export const AdminEpsteinEntities = ({ token, userToken = null }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("kind") || ""; }
    catch { return ""; }
  });
  // Context Card Foundation — editorial coverage filter (all | has | missing).
  const [contextFilter, setContextFilter] = useState(() => {
    try {
      const c = new URLSearchParams(window.location.search).get("context");
      return ["has", "missing"].includes(c) ? c : "all";
    } catch { return "all"; }
  });
  const [selected, setSelected] = useState(null); // currently-edited entity
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [publishingRoth, setPublishingRoth] = useState(false);
  const [rothResult, setRothResult] = useState(null);
  const [publishingMixed, setPublishingMixed] = useState(false);
  const [mixedResult, setMixedResult] = useState(null);
  const [publishingFlights, setPublishingFlights] = useState(false);
  const [flightsResult, setFlightsResult] = useState(null);
  const [zorroBusy, setZorroBusy] = useState(false);
  const [zorroResult, setZorroResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await efAdminListEntities(token, userToken);
      setRows(r.rows || []);
    } catch {
      toast.error("Failed to load entities");
    } finally {
      setLoading(false);
    }
  }, [token, userToken]);

  const runBundledRestore = useCallback(async () => {
    if (!window.confirm(
      "Run bundled batch restore?\n\n" +
      "This will:\n" +
      "  • Ensure the Jeffrey Epstein anchor entity exists\n" +
      "  • Import + publish Batch 1 (real anchors) and Batch 2 (Lutnick cluster)\n\n" +
      "Idempotent — safe to re-run. New batches will be created in the import log."
    )) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const res = await efAdminRestoreBundledBatches(token, userToken);
      setRestoreResult(res);
      toast.success("Restore complete");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Restore failed";
      toast.error(msg);
      setRestoreResult({ ok: false, error: msg });
    } finally {
      setRestoring(false);
    }
  }, [token, userToken, load]);

  const publishRothschildLane = useCallback(async () => {
    if (!window.confirm(
      "Publish the Rothschild Lane (Batch 3a)?\n\n" +
      "This will materialize and PUBLISH the source-backed 2015 Southern Trust ↔ Edmond de Rothschild Holding S.A. cluster:\n" +
      "  • 3 entities (Ariane de Rothschild, EDRH, Southern Trust Company)\n" +
      "  • 4 relationships (Southern Trust↔Epstein, EDRH↔Southern Trust, Ariane↔Epstein, EDRH↔Epstein)\n" +
      "  • 5 sources (DOJ NPA, DOJ file 800771, Miami Herald, Anadolu Agency, Le Monde)\n" +
      "  • 3 timeline events + 3 evidence items\n" +
      "  • 5 evidence fragments (Anadolu LoA verbatim, MH Dec wire, DOJ NPA, 2x verbatim emails)\n\n" +
      "Idempotent. Re-running converges to the published state. The broader Batch 3 (Rothschild & Co / 2017 Deutsche Bank inquiry) is intentionally NOT touched — those items remain hold-review."
    )) return;
    setPublishingRoth(true);
    setRothResult(null);
    try {
      const res = await efAdminPublishRothschildLane(token, userToken);
      setRothResult(res);
      toast.success("Rothschild Lane published");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Rothschild publish failed";
      toast.error(msg);
      setRothResult({ ok: false, error: msg });
    } finally {
      setPublishingRoth(false);
    }
  }, [token, userToken, load]);

  const publishFirstMixedLane = useCallback(async () => {
    if (!window.confirm(
      "Publish the First Mixed-Evidence Lane (Batch 4)?\n\n" +
      "This will materialize and PUBLISH 6 source-backed mixed-evidence nodes:\n" +
      "  • U.S. v. Ghislaine Maxwell (20-cr-330) — legal_record\n" +
      "  • Giuffre v. Maxwell — Unsealed Court Documents — document\n" +
      "  • Epstein Flight Logs (Maxwell Trial Exhibits) — document\n" +
      "  • Foreign Passport Recovered at 9 East 71st Street — document\n" +
      "  • Boeing 727-31 (N908JE) — aircraft\n" +
      "  • Epstein–Maxwell Trial Email Exhibits — email\n\n" +
      "Plus 5 sources (Maxwell indictment, Giuffre v. Maxwell unsealed, DOJ JE indictment, FAA registry, Wikipedia) and 10 relationships connecting to Epstein and Maxwell.\n\n" +
      "Idempotent. Re-running converges to the published state."
    )) return;
    setPublishingMixed(true);
    setMixedResult(null);
    try {
      const res = await efAdminPublishFirstMixedLane(token, userToken);
      setMixedResult(res);
      toast.success("First Mixed-Evidence Lane published");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Mixed-lane publish failed";
      toast.error(msg);
      setMixedResult({ ok: false, error: msg });
    } finally {
      setPublishingMixed(false);
    }
  }, [token, userToken, load]);

  const publishFlightLogsStarter = useCallback(async () => {
    if (!window.confirm(
      "Publish the Flight Logs Starter (Batch 5)?\n\n" +
      "This will materialize and PUBLISH the starter flight-log dataset:\n" +
      "  • 3 aircraft (Boeing 727-31 N908JE, Gulfstream IV N909JE, Gulfstream G550 N212JE)\n" +
      "  • 6 representative flight events spanning 1997–2010\n" +
      "  • 12 passenger appearances (Epstein, Maxwell, Roberts Giuffre pre-linked; others left unresolved)\n" +
      "  • 5 sources (FAA registry × 3, Wikipedia, Maxwell trial docket)\n" +
      "  • 3 aircraft-ownership relationships\n\n" +
      "All records flagged `secondary_compilation`. Idempotent — re-running converges."
    )) return;
    setPublishingFlights(true);
    setFlightsResult(null);
    try {
      const res = await efAdminPublishFlightLogsStarter(token, userToken);
      setFlightsResult(res);
      toast.success("Flight Logs Starter published");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Flight Logs starter publish failed";
      toast.error(msg);
      setFlightsResult({ ok: false, error: msg });
    } finally {
      setPublishingFlights(false);
    }
  }, [token, userToken, load]);

  const enableZorroRanchStub = useCallback(async () => {
    if (!window.confirm(
      "Enable Zorro Ranch board stub?\n\n" +
      "This will:\n" +
      "  • Flip Zorro Ranch to public=true\n" +
      "  • Clear lifecycle_status (so it's no longer review-gated)\n" +
      "  • Set dossier_status to 'review' (if not already 'published')\n\n" +
      "The full dossier prose remains hidden behind the editorial-development stub. " +
      "No other entity is touched. Idempotent — safe to re-run."
    )) return;
    setZorroBusy(true);
    setZorroResult(null);
    try {
      const res = await efAdminEnableZorroRanchStub(token, userToken);
      setZorroResult(res);
      if (res?.stub_route_verified) {
        toast.success("Zorro Ranch stub enabled");
      } else {
        toast.warning("Operation ran but verification flag is false");
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Zorro Ranch stub enable failed";
      toast.error(msg);
      setZorroResult({ ok: false, error: msg });
    } finally {
      setZorroBusy(false);
    }
  }, [token, userToken, load]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = rows;
    if (contextFilter === "has") base = base.filter((e) => (e.context_summary || "").trim());
    if (contextFilter === "missing") base = base.filter((e) => !(e.context_summary || "").trim());
    if (!q) return base;
    return base.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.slug || "").toLowerCase().includes(q) ||
        (e.kind || "").toLowerCase().includes(q) ||
        (e.category_label || "").toLowerCase().includes(q),
    );
  }, [rows, query, contextFilter]);

  // After a save we replace the row in-place so the list keeps its current order.
  const onPatched = useCallback((updated) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
    setSelected(updated);
  }, []);

  if (selected) {
    return (
      <EntityEditor
        entity={selected}
        token={token}
        userToken={userToken}
        onBack={() => setSelected(null)}
        onPatched={onPatched}
      />
    );
  }

  return (
    <div data-testid="ef-admin-entities-panel">
      {/* Restore tool (mobile-friendly, no terminal needed) */}
      <div className="border border-[#E0A526]/40 bg-[#1a1208] mb-3 p-3" data-testid="ef-admin-restore-bundled">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E0A526] mb-1.5">
          // BUNDLED DATA RESTORE
        </div>
        <p className="text-[11px] text-[#CBB892] leading-relaxed mb-2">
          One-tap restore for production. Ensures the Jeffrey Epstein anchor exists, then imports
          and publishes Batch 1 + Batch 2 from the bundled JSON files. Idempotent — safe to re-run.
        </p>
        <button
          onClick={runBundledRestore}
          disabled={restoring}
          data-testid="ef-admin-restore-bundled-btn"
          className={
            "inline-flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] " +
            (restoring
              ? "bg-[#2D2A26] text-[#5a544a] cursor-wait"
              : "bg-[#E0A526] hover:bg-[#C58A0F] text-[#0F0F0F]")
          }
        >
          {restoring ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
          {restoring ? "Restoring…" : "Run Bundled Restore"}
        </button>
        {restoreResult && (
          <pre
            className="mt-3 border border-[#454038] bg-[#0a0907] p-2 font-mono text-[10px] text-[#CBB892] overflow-x-auto max-h-[40vh]"
            data-testid="ef-admin-restore-result"
          >
{JSON.stringify(
  {
    ok: restoreResult.ok,
    anchor: restoreResult.anchor_after,
    final_public_stats: restoreResult.final_public_stats,
    batches: (restoreResult.batches || []).map((b) => ({
      label: b.label,
      status: b.status,
      published: b.published,
      error: b.error,
    })),
  },
  null,
  2,
)}
          </pre>
        )}
      </div>

      {/* Rothschild Lane publication (Track 1 / production) */}
      <div className="border border-[#D92D20]/60 bg-[#1a0d0c] mb-3 p-3" data-testid="ef-admin-publish-rothschild">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1.5">
          // ROTHSCHILD LANE — 2015 SOUTHERN TRUST / EDRH
        </div>
        <p className="text-[11px] text-[#CBB892] leading-relaxed mb-2">
          Publishes ONLY the source-backed 2015 Rothschild cluster (Batch 3a): DOJ Swiss Bank
          Program NPA, the Oct 5 2015 Letter of Agreement, December 2015 $25M wire, and the
          documented 2013–2019 correspondence — corroborated by DOJ records, Miami Herald,
          Anadolu Agency, and Le Monde. Also seeds the 5 Batch 3a evidence fragments (Anadolu
          LoA verbatim, MH December wire, DOJ NPA, two verbatim Miami-Herald-reproduced emails).
          Broader Batch 3 (Rothschild &amp; Co / 2017 Deutsche Bank inquiry) is intentionally NOT
          touched. Idempotent.
        </p>
        <button
          onClick={publishRothschildLane}
          disabled={publishingRoth}
          data-testid="ef-admin-publish-rothschild-btn"
          className={
            "inline-flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] " +
            (publishingRoth
              ? "bg-[#2D2A26] text-[#5a544a] cursor-wait"
              : "bg-[#D92D20] hover:bg-[#A8221A] text-[#F7F4ED]")
          }
        >
          {publishingRoth ? <Loader2 size={12} className="animate-spin" /> : <FileSearch size={12} />}
          {publishingRoth ? "Publishing…" : "Publish Rothschild Lane"}
        </button>
        {rothResult && (
          <pre
            className="mt-3 border border-[#5a1611] bg-[#0a0907] p-2 font-mono text-[10px] text-[#CBB892] overflow-x-auto max-h-[40vh]"
            data-testid="ef-admin-publish-rothschild-result"
          >
{JSON.stringify(
  {
    ok: rothResult.ok,
    anchor: rothResult.anchor_after,
    final_public_stats: rothResult.final_public_stats,
    fragments: rothResult.fragments,
    batches: (rothResult.batches || []).map((b) => ({
      label: b.label,
      status: b.status,
      published: b.published,
      error: b.error,
    })),
  },
  null,
  2,
)}
          </pre>
        )}
      </div>

      {/* First Mixed-Evidence Lane (Track B / production) */}
      <div className="border border-[#5DD4B3]/50 bg-[#0a0f0e] mb-3 p-3" data-testid="ef-admin-publish-mixed">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-1.5">
          // FIRST MIXED-EVIDENCE LANE — FEDERAL / FLIGHTS / PASSPORT / EMAILS
        </div>
        <p className="text-[11px] text-[#CBB892] leading-relaxed mb-2">
          Publishes the first six non-person nodes onto the public Evidence Board (Batch 4):
          <strong className="text-[#E4DFD3]"> U.S. v. Maxwell 20-cr-330</strong>,
          <strong className="text-[#E4DFD3]"> Giuffre v. Maxwell unsealed docs</strong>,
          <strong className="text-[#E4DFD3]"> Epstein flight logs</strong>,
          <strong className="text-[#E4DFD3]"> Foreign passport (9 East 71st)</strong>,
          <strong className="text-[#E4DFD3]"> Boeing 727-31 (N908JE)</strong>,
          <strong className="text-[#E4DFD3]"> Maxwell trial email exhibits</strong>.
          All entries anchored to existing public DOJ / SDNY / FAA / Wikipedia records.
          Idempotent.
        </p>
        <button
          onClick={publishFirstMixedLane}
          disabled={publishingMixed}
          data-testid="ef-admin-publish-mixed-btn"
          className={
            "inline-flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] " +
            (publishingMixed
              ? "bg-[#2D2A26] text-[#5a544a] cursor-wait"
              : "bg-[#5DD4B3] hover:bg-[#3FA188] text-[#0a0907]")
          }
        >
          {publishingMixed ? <Loader2 size={12} className="animate-spin" /> : <FileSearch size={12} />}
          {publishingMixed ? "Publishing…" : "Publish First Mixed-Evidence Lane"}
        </button>
        {mixedResult && (
          <pre
            className="mt-3 border border-[#5DD4B3]/40 bg-[#0a0907] p-2 font-mono text-[10px] text-[#CBB892] overflow-x-auto max-h-[40vh]"
            data-testid="ef-admin-publish-mixed-result"
          >
{JSON.stringify(
  {
    ok: mixedResult.ok,
    final_public_stats: mixedResult.final_public_stats,
    batches: (mixedResult.batches || []).map((b) => ({
      label: b.label,
      status: b.status,
      published: b.published,
      error: b.error,
    })),
  },
  null,
  2,
)}
          </pre>
        )}
      </div>

      {/* Flight Logs Starter (Batch 5) */}
      <div className="border border-[#5DD4B3]/50 bg-[#0a0f0e] mb-3 p-3" data-testid="ef-admin-publish-flights">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5DD4B3] mb-1.5">
          // FLIGHT LOGS STARTER — AIRCRAFT / FLIGHT EVENTS / PASSENGERS
        </div>
        <p className="text-[11px] text-[#CBB892] leading-relaxed mb-2">
          Seeds the public Flight Logs section (Batch 5): 3 aircraft (N908JE / N909JE / N212JE)
          anchored to the FAA registry, 6 representative flight events 1997–2010, 12 passenger
          appearances (Epstein, Maxwell, and Virginia Roberts Giuffre pre-linked; others left
          unresolved), and 5 sources. All flagged
          <code className="mx-1 text-[#E0A526]">secondary_compilation</code> — editors upgrade to
          court-exhibit / primary-log as primary documents are attached. Idempotent.
        </p>
        <button
          onClick={publishFlightLogsStarter}
          disabled={publishingFlights}
          data-testid="ef-admin-publish-flights-btn"
          className={
            "inline-flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] " +
            (publishingFlights
              ? "bg-[#2D2A26] text-[#5a544a] cursor-wait"
              : "bg-[#5DD4B3] hover:bg-[#3FA188] text-[#0a0907]")
          }
        >
          {publishingFlights ? <Loader2 size={12} className="animate-spin" /> : <FileSearch size={12} />}
          {publishingFlights ? "Publishing…" : "Publish Flight Logs Starter"}
        </button>
        {flightsResult && (
          <pre
            className="mt-3 border border-[#5DD4B3]/40 bg-[#0a0907] p-2 font-mono text-[10px] text-[#CBB892] overflow-x-auto max-h-[40vh]"
            data-testid="ef-admin-publish-flights-result"
          >
{JSON.stringify(
  {
    ok: flightsResult.ok,
    final_public_stats: flightsResult.final_public_stats,
    passengers: flightsResult.passengers,
    batches: (flightsResult.batches || []).map((b) => ({
      label: b.label,
      status: b.status,
      published: b.published,
      error: b.error,
    })),
  },
  null,
  2,
)}
          </pre>
        )}
      </div>

      {/* Zorro Ranch board stub (one-off, idempotent) */}
      <div
        className="border border-[#E0A526]/50 bg-[#1a1408] mb-3 p-3"
        data-testid="ef-admin-zorro-ranch-stub"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E0A526] mb-1.5">
          // ZORRO RANCH — ENABLE BOARD STUB
        </div>
        <p className="text-[11px] text-[#CBB892] leading-relaxed mb-2">
          Flips Zorro Ranch to <code className="text-[#E0A526]">public=true</code>, clears
          <code className="text-[#E0A526]"> lifecycle_status</code>, and sets
          <code className="text-[#E0A526]"> dossier_status=&quot;review&quot;</code> (if not already
          <code className="text-[#5DD4B3]"> &quot;published&quot;</code>). The dossier prose remains
          hidden behind the editorial-development stub on the public Evidence Board.
          Does NOT publish the long-form dossier. Touches only Zorro Ranch. Idempotent.
        </p>
        <button
          onClick={enableZorroRanchStub}
          disabled={zorroBusy}
          data-testid="ef-admin-zorro-ranch-stub-btn"
          className={
            "inline-flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] " +
            (zorroBusy
              ? "bg-[#2D2A26] text-[#5a544a] cursor-wait"
              : "bg-[#E0A526] hover:bg-[#C58A0F] text-[#0F0F0F]")
          }
        >
          {zorroBusy ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
          {zorroBusy ? "Enabling…" : "Enable Zorro Ranch Board Stub"}
        </button>
        {zorroResult && (
          <pre
            className="mt-3 border border-[#E0A526]/40 bg-[#0a0907] p-2 font-mono text-[10px] text-[#CBB892] overflow-x-auto max-h-[40vh]"
            data-testid="ef-admin-zorro-ranch-stub-result"
          >
{JSON.stringify(
  {
    ok: zorroResult.ok,
    slug: zorroResult.slug,
    entity_id: zorroResult.entity_id,
    before_state: zorroResult.before_state,
    after_state: zorroResult.after_state,
    public_board_visible: zorroResult.public_board_visible,
    stub_route_verified: zorroResult.stub_route_verified,
    notes: zorroResult.notes,
    error: zorroResult.error,
  },
  null,
  2,
)}
          </pre>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
          // ENTITIES ({rows.length})
        </div>
        <button
          onClick={load}
          data-testid="ef-admin-entities-refresh"
          className="ml-auto flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="relative mb-2">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5a544a]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, slug, kind, category…"
          data-testid="ef-admin-entities-search"
          className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none pl-7 pr-3 py-2 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
        />
      </div>

      {/* Context Summary coverage filters — work alongside the search box */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ef-admin-context-filters">
        {[["all", "All"],
          ["has", `Has Context (${rows.filter((e) => (e.context_summary || "").trim()).length})`],
          ["missing", `Missing Context (${rows.filter((e) => !(e.context_summary || "").trim()).length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setContextFilter(k)}
                  data-testid={`ef-admin-context-filter-${k}`}
                  className="px-2.5 min-h-[36px] border font-mono text-[9px] uppercase tracking-[0.15em] transition-colors"
                  style={{
                    borderColor: contextFilter === k ? "#D9A56C" : "#2D2A26",
                    color: contextFilter === k ? "#D9A56C" : "#7B7168",
                    background: contextFilter === k ? "#181205" : "transparent",
                  }}>
            {label}
          </button>
        ))}
      </div>

      {loading && rows.length === 0 ? (
        <div className="font-mono text-xs text-[#757065]">// LOADING…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ef-admin-entities-list">
          {filtered.map((e) => (
            <button
              key={e.id}
              onClick={() => setSelected(e)}
              data-testid={`ef-admin-entity-row-${e.id}`}
              className="flex gap-3 items-center text-left bg-[#0F0F0F] border border-[#2D2A26] hover:border-[#D92D20] transition-colors px-3 py-2"
            >
              <div className="w-12 h-14 border border-[#2D2A26] bg-[#0a0907] shrink-0 overflow-hidden relative">
                {e.photo_url || e.thumbnail_url || e.image_url ? (
                  <img
                    src={e.thumbnail_url || e.photo_url || e.image_url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[#454038]">
                    <ImageOff size={16} strokeWidth={1.2} />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm text-[#E4DFD3] truncate">{e.name}</div>
                <div className="font-mono text-[9px] text-[#757065] uppercase tracking-[0.25em] truncate">
                  {e.kind} {e.is_anchor ? "· ANCHOR" : ""} {e.public ? "· PUBLIC" : "· DRAFT"}
                </div>
                {e.category_label && (
                  <div className="font-mono text-[9px] text-[#A39E93] uppercase tracking-[0.2em] truncate">
                    {e.category_label}
                  </div>
                )}
              </div>
              <span className="font-mono text-[9px] text-[#D92D20] uppercase tracking-[0.25em] shrink-0">
                EDIT →
              </span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-[#757065] font-mono text-xs uppercase tracking-widest py-8 border border-dashed border-[#2D2A26]">
              No entities match.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// EntityEditor — photo_url / thumbnail_url editor with live preview
// =============================================================================
// Phase 2 field definitions: which extra fields to surface per kind. Each row
// is `[backendKey, label, placeholder?]`. Keep it short, mobile-friendly, and
// strictly tied to fields already on the EntityPatch schema.
const PHASE2_FIELDS_BY_KIND = {
  property:        [["property_type", "Property type", "ranch / island / townhouse"], ["location_label", "Location", "New Mexico, USA"], ["country", "Country"], ["region", "Region / city"]],
  location:        [["property_type", "Type", "city / region"], ["location_label", "Location"], ["country", "Country"], ["region", "Region"]],
  financial_entity:[["financial_type", "Subtype", "holding / fund / trust"], ["jurisdiction", "Jurisdiction", "Switzerland · USVI"], ["incorporation_location", "Incorporation location"]],
  shell_company:   [["financial_type", "Subtype", "shell company"], ["jurisdiction", "Jurisdiction"], ["incorporation_location", "Incorporation location"]],
  trust:           [["financial_type", "Subtype", "trust"], ["jurisdiction", "Jurisdiction"], ["incorporation_location", "Incorporation location"]],
  company:         [["org_type", "Type", "private bank / advisory"], ["jurisdiction", "Jurisdiction"]],
  account:         [["financial_type", "Subtype", "brokerage / bank account"], ["jurisdiction", "Jurisdiction"]],
  aircraft:        [["tail_number", "Tail number", "N908JE"], ["aircraft_model", "Model", "Boeing 727-31"], ["operator_label", "Operator"]],
  flight:          [["tail_number", "Tail number"], ["aircraft_model", "Model"], ["operator_label", "Operator"]],
  flight_event:    [["flight_date_start", "Flight date", "2002-09-11"], ["origin_label", "Origin", "TEB"], ["destination_label", "Destination", "PBI"], ["route_label", "Route label"], ["manifest_reference", "Manifest reference"]],
  document:        [["document_type", "Document type", "letter / agreement / memo"], ["document_date", "Document date", "2015-10-05"], ["origin_source_label", "Origin / source label", "DOJ release · file 800771"], ["file_url", "File URL"]],
  legal_record:    [["legal_type", "Subtype", "indictment / settlement / NPA"], ["court_or_venue", "Court / venue", "S.D.N.Y."], ["filing_date", "Filed date"], ["resolution_date", "Resolved date"]],
  filing:          [["legal_type", "Subtype", "complaint / motion"], ["court_or_venue", "Court / venue"], ["filing_date", "Filed date"], ["resolution_date", "Resolved date"]],
  organization:    [["org_type", "Type", "regulator / agency / NGO"], ["jurisdiction", "Jurisdiction"]],
  timeline_event:  [["event_date", "Event date"], ["event_start", "Range start"], ["event_end", "Range end"]],
  event:           [["event_date", "Event date"], ["event_start", "Range start"], ["event_end", "Range end"]],
};

const EntityEditor = ({ entity, token, userToken, onBack, onPatched }) => {
  const [imageUrl, setImageUrl] = useState(entity.image_url || "");
  const [imageAlt, setImageAlt] = useState(entity.image_alt || "");
  const [imageSource, setImageSource] = useState(entity.image_source || "");
  const [photoUrl, setPhotoUrl] = useState(entity.photo_url || "");
  const [thumbUrl, setThumbUrl] = useState(entity.thumbnail_url || "");
  const [saving, setSaving] = useState(false);
  // Editorial visibility state — independent controls.
  // `publicVisible` drives the entity's presence on the public Evidence
  // Board / Directory / Search. `dossierStatus` drives whether the
  // long-form dossier prose is readable to the public.
  const [publicVisible, setPublicVisible] = useState(!!entity.public);
  const [dossierStatus, setDossierStatus] = useState(entity.dossier_status || (entity.public ? "published" : "draft"));
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  // Context Card Foundation — official short description for Connections.
  const [contextSummary, setContextSummary] = useState(entity.context_summary || "");

  // Phase 2 kind-specific fields. Only render fields relevant to entity.kind.
  // Backwards compatible — fields default to empty and are skipped on save if unchanged.
  const phase2Defs = PHASE2_FIELDS_BY_KIND[entity.kind] || [];
  const [phase2Values, setPhase2Values] = useState(() => {
    const initial = {};
    phase2Defs.forEach(([key]) => { initial[key] = entity[key] || ""; });
    return initial;
  });

  // Upload state — independent per field so the user can run both at once.
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress, setPhotoProgress] = useState(0);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [thumbProgress, setThumbProgress] = useState(0);
  const imageInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const dirty =
    (imageUrl || "") !== (entity.image_url || "") ||
    (imageAlt || "") !== (entity.image_alt || "") ||
    (imageSource || "") !== (entity.image_source || "") ||
    (photoUrl || "") !== (entity.photo_url || "") ||
    (thumbUrl || "") !== (entity.thumbnail_url || "") ||
    (contextSummary || "") !== (entity.context_summary || "") ||
    phase2Defs.some(([k]) => (phase2Values[k] || "") !== (entity[k] || ""));

  const uploadFile = async (file, target) => {
    const setUploading =
      target === "image" ? setImageUploading
      : target === "photo" ? setPhotoUploading
      : setThumbUploading;
    const setProgress =
      target === "image" ? setImageProgress
      : target === "photo" ? setPhotoProgress
      : setThumbProgress;
    const setUrl =
      target === "image" ? setImageUrl
      : target === "photo" ? setPhotoUrl
      : setThumbUrl;
    // Friendly client-side checks (server still re-validates).
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (file.type && !allowed.includes(file.type)) {
      toast.error(`Unsupported file type: ${file.type}. JPG, PNG, WebP only.`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5 MB.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const res = await efAdminUploadEntityImage(token, file, userToken, setProgress);
      if (res?.public_url) {
        setUrl(res.public_url);
        toast.success(`Uploaded · ${(res.size_bytes / 1024).toFixed(0)} KB`);
      } else {
        toast.error("Upload returned no URL");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        image_url: imageUrl || null,
        image_alt: imageAlt || null,
        image_source: imageSource || null,
        photo_url: photoUrl || null,
        thumbnail_url: thumbUrl || null,
      };
      if ((contextSummary || "") !== (entity.context_summary || "")) {
        patch.context_summary = contextSummary;
      }
      phase2Defs.forEach(([k]) => {
        const v = phase2Values[k];
        if ((v || "") !== (entity[k] || "")) {
          patch[k] = v || null;
        }
      });
      const updated = await efAdminPatchEntity(token, entity.id, patch, userToken);
      toast.success("Entity saved");
      onPatched(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeImage = async () => {
    if (!entity.image_url) return;
    if (!window.confirm("Remove the expanded image? The fallback evidence-board photo (if any) will remain.")) return;
    setSaving(true);
    try {
      const updated = await efAdminPatchEntity(
        token, entity.id,
        { image_url: null, image_alt: null, image_source: null },
        userToken,
      );
      setImageUrl(""); setImageAlt(""); setImageSource("");
      toast.success("Expanded image removed");
      onPatched(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setSaving(false);
    }
  };

  // Save Entity Visibility + Dossier Status independently of the image
  // editor. We use a dedicated save so a one-click visibility flip doesn't
  // require the editor to be "dirty" in the image fields.
  const saveVisibility = async (patch) => {
    setVisibilitySaving(true);
    try {
      const updated = await efAdminPatchEntity(token, entity.id, patch, userToken);
      if (patch.public !== undefined) setPublicVisible(!!updated.public);
      if (patch.dossier_status !== undefined) {
        setDossierStatus(updated.dossier_status || "draft");
      }
      toast.success("Visibility updated");
      onPatched(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Visibility update failed");
    } finally {
      setVisibilitySaving(false);
    }
  };

  const togglePublic = () => {
    const next = !publicVisible;
    if (!next && entity.public) {
      if (!window.confirm(
        "Hide this entity from the public Evidence Board?\n\n" +
        "Dependent public records (relationships, evidence, timeline) attached " +
        "to this entity will be cascade-demoted to hidden. Re-publishing later " +
        "is non-destructive (history is preserved)."
      )) return;
    }
    saveVisibility({ public: next });
  };

  const onDossierStatusChange = (next) => {
    if (next === dossierStatus) return;
    saveVisibility({ dossier_status: next });
  };

  return (
    <div data-testid="ef-admin-entity-editor">
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onBack}
          data-testid="ef-admin-entity-back"
          className="flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <ChevronLeft size={12} /> Back
        </button>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] truncate">
          // ENTITY · {entity.slug}
        </div>
      </div>

      <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3">
        <div className="font-heading text-xl text-[#E4DFD3]">{entity.name}</div>
        <div className="font-mono text-[10px] text-[#757065] uppercase tracking-[0.25em] mt-0.5">
          {entity.kind} {entity.is_anchor ? "· ANCHOR" : ""} {publicVisible ? "· PUBLIC" : "· HIDDEN"} · DOSSIER {dossierStatus.toUpperCase()}
        </div>
        {entity.summary && (
          <div className="text-[12px] text-[#A39E93] mt-2 line-clamp-3">{entity.summary}</div>
        )}
        <div className="mt-2">
          <MediaAttachButton token={token} userToken={userToken}
                             targetType={kindToMediaType(entity.kind)}
                             targetId={entity.slug} recordName={entity.name} />
        </div>
      </div>

      {/* Context Summary — Context Card Foundation (Connections Explorer) */}
      <div className="border border-[#2D2A26] bg-[#0a0907]/70 p-3 mb-3"
           data-testid="ef-admin-context-summary-panel">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1.5 flex items-center gap-2">
          // CONTEXT SUMMARY
          {(entity.context_summary || "").trim() ? (
            <span className="font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 bg-[#3FB950] text-[#0a0907]"
                  data-testid="ef-admin-context-state-published">✓ Published</span>
          ) : (
            <span className="font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-[#D9A56C] text-[#D9A56C]"
                  data-testid="ef-admin-context-state-needed">Needs Summary</span>
          )}
        </div>
        <p className="text-[11px] text-[#A39E93] mb-2 leading-relaxed">
          The official short description used inside the Connections Explorer.
          Plain text, ~50–120 words. Independent from the summary, biography and
          dossier fields. Public only while this entity is public.
        </p>
        <textarea value={contextSummary}
                  onChange={(e) => setContextSummary(e.target.value)}
                  rows={4} maxLength={1200}
                  placeholder="Concise editorial context for this record…"
                  data-testid="ef-admin-context-summary-input"
                  className="w-full bg-[#0F0E0B] border border-[#2D2A26] px-2.5 py-2 text-[13px] text-[#E4DFD3] leading-relaxed placeholder:text-[#4A463F] focus:outline-none focus:border-[#A68F5E]" />
        <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#5A554C] mt-1"
             data-testid="ef-admin-context-summary-wordcount">
          {contextSummary.trim().split(/\s+/).filter(Boolean).length} words · target 50–120
        </div>
      </div>

      {/* Editorial Visibility — independent controls */}
      <div
        className="border border-[#2D2A26] bg-[#0a0907]/70 p-3 mb-3"
        data-testid="ef-admin-entity-visibility-panel"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">
          // EDITORIAL VISIBILITY
        </div>
        <p className="text-[11px] text-[#A39E93] mb-3 leading-relaxed">
          <span className="text-[#E4DFD3]">Entity Visibility</span> controls
          whether this record appears on the public Evidence Board, Directory,
          Graph, and Search.{" "}
          <span className="text-[#E4DFD3]">Dossier Status</span> controls
          whether the long-form dossier prose is readable to the public — these
          are independent. A public entity can have a draft dossier; visitors
          will see a stub with the source trail until the dossier is published.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          {/* Entity Visibility */}
          <div className="border border-[#2D2A26] p-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-1.5">
              Entity Visibility
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={togglePublic}
                disabled={visibilitySaving}
                data-testid="ef-admin-entity-visibility-toggle"
                className={
                  "flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.3em] border " +
                  (publicVisible
                    ? "bg-[#0c1a0e] border-[#5DD4B3] text-[#5DD4B3]"
                    : "bg-[#1a0a0a] border-[#D92D20] text-[#D92D20]")
                }
              >
                {visibilitySaving ? <Loader2 size={11} className="animate-spin" /> : null}
                {publicVisible ? "PUBLIC" : "HIDDEN"}
              </button>
              <span className="font-mono text-[9px] text-[#757065]">
                {publicVisible
                  ? "appears on the public Evidence Board"
                  : "hidden from public — admin only"}
              </span>
            </div>
          </div>

          {/* Dossier Status */}
          <div className="border border-[#2D2A26] p-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-1.5">
              Dossier Status
            </div>
            <div
              className="inline-flex border border-[#2D2A26]"
              data-testid="ef-admin-entity-dossier-status"
            >
              {[
                ["draft", "DRAFT"],
                ["review", "REVIEW"],
                ["published", "PUBLISHED"],
              ].map(([k, l]) => {
                const active = dossierStatus === k;
                const color = k === "published"
                  ? "#5DD4B3"
                  : k === "review"
                  ? "#E0A526"
                  : "#A39E93";
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => onDossierStatusChange(k)}
                    disabled={visibilitySaving}
                    data-testid={`ef-admin-entity-dossier-${k}`}
                    style={active ? { color, borderColor: color } : undefined}
                    className={
                      "px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] border-r border-[#2D2A26] last:border-r-0 " +
                      (active
                        ? "bg-[#0a0907]"
                        : "text-[#A39E93] hover:text-[#E4DFD3]")
                    }
                  >
                    {l}
                  </button>
                );
              })}
            </div>
            <div className="mt-1.5 font-mono text-[9px] text-[#757065] leading-snug">
              {dossierStatus === "published"
                ? "long-form dossier visible to the public"
                : dossierStatus === "review"
                ? "ready for editorial review — admin only"
                : "editorial development — admin only"}
            </div>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="grid grid-cols-3 gap-3 mb-3" data-testid="ef-admin-entity-preview">
        <PreviewBox label="// EXPANDED IMAGE" url={imageUrl} aspect="aspect-[4/3]" testid="ef-preview-image" />
        <PreviewBox label="// PHOTO (FULL)" url={photoUrl} aspect="aspect-[4/5]" testid="ef-preview-photo" />
        <PreviewBox label="// THUMBNAIL" url={thumbUrl || photoUrl} aspect="aspect-square" testid="ef-preview-thumb" />
      </div>

      <div className="space-y-3">
        {/* Upload guard / editorial warning */}
        <div className="border-l-2 border-[#E0A526] pl-3 text-[11px] italic text-[#CBB892]">
          ⚠ Use only copyright-safe images you have rights to use: public-domain, government, Wikimedia Commons,
          or licensed images with attribution. JPG, PNG, or WebP. Max 5 MB.
        </div>

        {/* Expanded image (shown on the entity detail page + property dossier) */}
        <div className="border border-[#2D2A26] bg-[#0a0907]/60 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
              // EXPANDED IMAGE SLOT
            </div>
            {entity.image_url && (
              <button
                type="button"
                onClick={removeImage}
                disabled={saving}
                data-testid="ef-admin-entity-image-remove"
                className="inline-flex items-center gap-1.5 border border-[#E0A526] hover:bg-[#E0A526]/10 text-[#E0A526] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.3em]"
              >
                Remove image
              </button>
            )}
          </div>

          <UploadField
            label="Image URL (used on the public expanded entity & property dossier pages)"
            value={imageUrl}
            onChange={(v) => { setImageUrl(v); }}
            placeholder="https://… or tap Upload Image"
            testid="ef-admin-entity-image-url"
            uploadTestid="ef-admin-entity-image-upload"
            uploadLabel="Upload Image"
            inputRef={imageInputRef}
            uploading={imageUploading}
            progress={imageProgress}
            onFile={(f) => uploadFile(f, "image")}
          />

          <label className="block mt-3">
            <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mb-1.5">
              Image alt text (accessibility — describe the image for screen readers)
            </span>
            <input
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              maxLength={300}
              data-testid="ef-admin-entity-image-alt"
              placeholder={`e.g. "Aerial view of ${entity.name}, looking south."`}
              className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
            />
          </label>

          <label className="block mt-3">
            <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mb-1.5">
              Image source / credit (shown as a small line beneath the image)
            </span>
            <input
              value={imageSource}
              onChange={(e) => setImageSource(e.target.value)}
              maxLength={500}
              data-testid="ef-admin-entity-image-source"
              placeholder={`e.g. "Wikimedia Commons — CC-BY-SA-3.0" or "Photo: NM DOJ"`}
              className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
            />
          </label>
        </div>

        <UploadField
          label="Photo URL (used on the Evidence Board card + detail panels)"
          value={photoUrl}
          onChange={(v) => { setPhotoUrl(v); }}
          placeholder="https://… or tap Upload Photo"
          testid="ef-admin-entity-photo-url"
          uploadTestid="ef-admin-entity-photo-upload"
          uploadLabel="Upload Photo"
          inputRef={photoInputRef}
          uploading={photoUploading}
          progress={photoProgress}
          onFile={(f) => uploadFile(f, "photo")}
        />
        <UploadField
          label="Thumbnail URL (optional — smaller image for list rows). Leave blank to reuse Photo URL."
          value={thumbUrl}
          onChange={(v) => { setThumbUrl(v); }}
          placeholder="https://… or tap Upload Thumbnail"
          testid="ef-admin-entity-thumb-url"
          uploadTestid="ef-admin-entity-thumb-upload"
          uploadLabel="Upload Thumbnail"
          inputRef={thumbInputRef}
          uploading={thumbUploading}
          progress={thumbProgress}
          onFile={(f) => uploadFile(f, "thumb")}
        />

        {/* Phase 2 — kind-specific facts (aircraft tail number / flight route / shell jurisdiction / etc.) */}
        {phase2Defs.length > 0 && (
          <div
            className="border border-[#2D2A26] bg-[#0a0907]/60 p-3"
            data-testid="ef-admin-entity-phase2-panel"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
              // {entity.kind?.toUpperCase().replace(/_/g, " ")} · RECORD FIELDS
            </div>
            <p className="text-[11px] text-[#A39E93] mb-3">
              Phase 2 fields surface on the Evidence Board card, the inline detail panel, and the
              public entity page. Leave blank to omit. All fields are source-grounded — do not
              speculate.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {phase2Defs.map(([key, label, placeholder]) => (
                <label key={key} className="block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mb-1.5">
                    {label}
                  </span>
                  <input
                    value={phase2Values[key] || ""}
                    onChange={(e) => setPhase2Values((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={placeholder || ""}
                    data-testid={`ef-admin-entity-phase2-${key}`}
                    className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
                  />
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 left-0 right-0 mt-4 bg-[#0a0907] border-t border-[#2D2A26] -mx-3 px-3 py-3 sm:static sm:bg-transparent sm:border-t-0 sm:px-0 sm:py-0 flex items-center gap-2">
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="ef-admin-entity-save"
          className={
            "flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] w-full sm:w-auto " +
            (dirty && !saving
              ? "bg-[#D92D20] hover:bg-[#B92318] text-white"
              : "bg-[#2D2A26] text-[#5a544a] cursor-not-allowed")
          }
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {saving ? "Saving…" : dirty ? "Save Changes" : "No changes"}
        </button>
        {(photoUrl || thumbUrl) && (
          <a
            href={photoUrl || thumbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]"
          >
            <ExternalLink size={11} /> Open URL
          </a>
        )}
      </div>

      <div className="border-l-2 border-[#E0A526] pl-3 mt-4 text-[11px] italic text-[#CBB892]">
        ⚠ Uploads are limited to 5 MB. Broken or removed URLs will render the &ldquo;no photo on file&rdquo;
        placeholder on the public board.
      </div>
    </div>
  );
};

const UploadField = ({
  label, value, onChange, placeholder, testid,
  uploadTestid, uploadLabel, inputRef, uploading, progress, onFile,
}) => (
  <label className="block">
    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mb-1.5">
      {label}
    </span>
    <div className="flex flex-col sm:flex-row gap-2">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        inputMode="url"
        className="flex-1 bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2.5 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={uploadTestid}
        className={
          "flex items-center justify-center gap-2 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] shrink-0 " +
          (uploading
            ? "bg-[#2D2A26] text-[#5a544a] cursor-wait"
            : "bg-[#D92D20] hover:bg-[#B92318] text-white")
        }
      >
        {uploading
          ? (<><Loader2 size={12} className="animate-spin" /> Uploading {progress > 0 ? `${progress}%` : "…"}</>)
          : (<><Upload size={12} /> {uploadLabel}</>)}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = ""; // allow same file twice
        }}
      />
    </div>
  </label>
);

const PreviewBox = ({ label, url, aspect, testid }) => (
  <div className="border border-[#2D2A26] bg-[#0a0907]" data-testid={testid}>
    <div className="px-2 py-1 border-b border-[#2D2A26] font-mono text-[8px] uppercase tracking-[0.3em] text-[#D92D20]">
      {label}
    </div>
    <div className={`${aspect} relative overflow-hidden`}>
      {url ? (
        <img
          src={url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            const sibling = e.currentTarget.nextElementSibling;
            if (sibling) sibling.style.display = "flex";
          }}
        />
      ) : null}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center text-[#454038]"
        style={{ display: url ? "none" : "flex" }}
      >
        <ImageOff size={28} strokeWidth={1.2} />
        <div className="font-mono text-[8px] uppercase tracking-[0.3em] mt-1.5 text-[#5a544a]">
          {url ? "load failed" : "no preview"}
        </div>
      </div>
    </div>
  </div>
);

export default AdminEpsteinEntities;
